import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
  selectHasPermission,
} from "../../store/slices/authSlice";
import { toLocalDateStr, canSeeAbsenceDetails } from "../../utils/absenceUtils";
import {
  useGetAppointmentsQuery,
  useGetAvailabilityForRangeQuery,
  useGetAbsencesQuery,
} from "../../store/services/calendarApi";
import { useGetPatientsQuery, PATIENTS_PICKER_PAGE_SIZE } from "../../store/services/patientsApi";
import WeekView from "./WeekView";
import AppointmentModal from "./AppointmentEntryModal";
import CalendarSettings from "./CalendarSettings";
import {
  CaretLeft,
  CaretRight,
  CalendarBlank,
  Clock,
  User,
} from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import ContentLoader from "../shared/ContentLoader";
import "./TherapistCalendarModal.css";

// Mobile breakpoint
const MOBILE_BREAKPOINT = 768;

/**
 * TherapistCalendarModal - Modal showing a therapist's weekly calendar
 * Used by clinic admin to view therapist calendar and schedule appointments
 */
function TherapistCalendarModal({ therapist, onClose, onAppointmentCreated, initialDate = null }) {
  const clinicId = useSelector(selectClinicId);
  const adminTherapistId = useSelector(selectTherapistId);
  const adminTherapistName = useSelector(selectTherapistName);
  const isAdmin = useSelector(selectIsAdmin);
  // Multi-clinic v2: non-admins with `calendar.editOthersAvailability` may
  // open the availability editor for another therapist.
  const canEditOthersAvailability = useSelector(
    selectHasPermission("calendar.editOthersAvailability")
  );

  const [currentDate, setCurrentDate] = useState(() => (initialDate ? new Date(initialDate) : new Date()));
  const [showAvailabilityEditor, setShowAvailabilityEditor] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState(null);
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [selectedDayIndex, setSelectedDayIndex] = useState(new Date().getDay());

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get therapist display name
  const therapistName =
    therapist.firstName && therapist.lastName
      ? `${therapist.firstName} ${therapist.lastName}`
      : therapist.displayName || therapist.name || "מטפל";

  // Get initials for avatar
  const initials =
    therapist.firstName && therapist.lastName
      ? `${therapist.firstName.charAt(0)}${therapist.lastName.charAt(0)}`
      : therapistName.charAt(0);

  // Calculate date range for the week
  const dateRange = useMemo(() => {
    const current = new Date(currentDate);
    const dayOfWeek = current.getDay();
    const start = new Date(current);
    start.setDate(current.getDate() - dayOfWeek);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [currentDate]);

  // Fetch appointments for the therapist
  const { data: appointments = [], isLoading, refetch: refetchAppointments } = useGetAppointmentsQuery(
    {
      clinicId,
      therapistId: therapist.id, // Filter by this specific therapist
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
    },
    { skip: !clinicId || !therapist.id }
  );

  // Fetch availability slots for the therapist
  const { data: availabilitySlots = [] } = useGetAvailabilityForRangeQuery(
    {
      therapistId: therapist.id,
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
    },
    { skip: !therapist.id }
  );

  // Absences of this therapist in range (multi-clinic v2)
  const { data: rangeAbsences = [] } = useGetAbsencesQuery(
    {
      clinicId,
      therapistId: therapist.id,
      from: toLocalDateStr(dateRange.start),
      to: toLocalDateStr(dateRange.end),
    },
    { skip: !clinicId || !therapist.id }
  );
  const absences = useMemo(
    () =>
      rangeAbsences.map((a) => ({
        ...a,
        canSeeDetails: canSeeAbsenceDetails(a, { viewerId: adminTherapistId, isAdmin }),
      })),
    [rangeAbsences, adminTherapistId, isAdmin]
  );

  // Fetch patients for this therapist only (for appointment modal)
  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, therapistId: therapist.id, pageSize: PATIENTS_PICKER_PAGE_SIZE },
    { skip: !clinicId || !therapist.id }
  );

  // Navigate to previous (week on desktop, day on mobile)
  const goToPrevious = () => {
    if (isMobile) {
      if (selectedDayIndex > 0) {
        setSelectedDayIndex(selectedDayIndex - 1);
      } else {
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() - 7);
        setCurrentDate(newDate);
        setSelectedDayIndex(6);
      }
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    }
  };

  // Navigate to next (week on desktop, day on mobile)
  const goToNext = () => {
    if (isMobile) {
      if (selectedDayIndex < 6) {
        setSelectedDayIndex(selectedDayIndex + 1);
      } else {
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + 7);
        setCurrentDate(newDate);
        setSelectedDayIndex(0);
      }
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    }
  };

  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
    if (isMobile) {
      setSelectedDayIndex(new Date().getDay());
    }
  };

  // Handle slot click - open appointment modal with prefilled date
  const handleSlotClick = (date) => {
    setPrefilledDate(date);
    setSelectedAppointment(null);
    setShowAppointmentModal(true);
  };

  // Handle appointment click - open modal for editing
  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setPrefilledDate(null);
    setShowAppointmentModal(true);
  };

  // Close appointment modal
  const handleCloseAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
    setPrefilledDate(null);
  };

  // Handle successful appointment creation - refetch appointments and notify parent
  const handleAppointmentSuccess = () => {
    refetchAppointments();
    if (onAppointmentCreated) {
      onAppointmentCreated();
    }
  };

  // Format header date display
  const formatHeaderDate = () => {
    const weekStart = dateRange.start;
    const weekEnd = dateRange.end;

    // Check if week spans two different months
    if (weekStart.getMonth() === weekEnd.getMonth()) {
      // Same month: "10-16 בינואר 2026"
      return `${weekStart.getDate()}-${weekEnd.getDate()} ${weekStart.toLocaleDateString(
        "he-IL",
        { month: "long", year: "numeric" }
      )}`;
    } else {
      // Different months: "28 בדצמבר - 3 בינואר 2026"
      const startStr = weekStart.toLocaleDateString("he-IL", {
        day: "numeric",
        month: "long",
      });
      const endStr = weekEnd.toLocaleDateString("he-IL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return `${startStr} - ${endStr}`;
    }
  };

  return (
    <div className="therapist-calendar-modal-overlay" onClick={onClose}>
      <div
        className="therapist-calendar-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="therapist-calendar-modal-header">
          <div className="header-right">
            <div className="therapist-info">
              <div className="therapist-avatar">
                <span className="avatar-initials">{initials}</span>
              </div>
              <div className="therapist-details">
                <h2>יומן {therapistName}</h2>
                <span className="therapist-email">{therapist.email}</span>
              </div>
            </div>
          </div>

          <div className="header-center">
            <div className="date-navigation">
              <button className="nav-btn" onClick={goToPrevious}>
                <CaretRight size={18} weight="bold" />
              </button>
              <button className="today-btn" onClick={goToToday}>
                <CalendarBlank size={16} weight="regular" />
                <span>היום</span>
              </button>
              <button className="nav-btn" onClick={goToNext}>
                <CaretLeft size={18} weight="bold" />
              </button>
            </div>
            <h3 className="current-date-display">{formatHeaderDate()}</h3>
          </div>

          <div className="header-left">
            {(isAdmin || canEditOthersAvailability === true) && (
              <button
                className="edit-availability-btn"
                onClick={() => setShowAvailabilityEditor(true)}
                title="עריכת זמינות וזמנים חסומים של המטפל"
              >
                <Clock size={16} weight="bold" />
                <span>עריכת זמינות</span>
              </button>
            )}
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        {/* Modal Body - Week View (Day View on mobile) */}
        <div className="therapist-calendar-modal-body">
          {isLoading ? (
            <ContentLoader fill message="טוען יומן..." />
          ) : (
            <WeekView
              startDate={dateRange.start}
              appointments={appointments}
              availabilitySlots={availabilitySlots}
              absences={absences}
              onSlotClick={handleSlotClick}
              onAppointmentClick={handleAppointmentClick}
              showTherapistName={true}
              singleDayMode={isMobile}
              selectedDayIndex={selectedDayIndex}
            />
          )}
        </div>

        {/* Availability Legend */}
        <div className="therapist-calendar-modal-footer">
          <div className="availability-legend">
            <div className="legend-item">
              <span className="legend-color available"></span>
              <span className="legend-label">זמינות מוגדרת</span>
            </div>
            <div className="legend-item">
              <span className="legend-color blocked"></span>
              <span className="legend-label">זמן חסום</span>
            </div>
            <div className="legend-item">
              <span className="legend-color past"></span>
              <span className="legend-label">זמן שעבר</span>
            </div>
          </div>
          <div className="patients-info">
            <User size={16} weight="fill" />
            <span>{patientsData?.patients?.length || 0} מטופלים</span>
          </div>
        </div>

        {/* Availability editor (admin edits this therapist's windows + blocks) */}
        {showAvailabilityEditor && (
          <CalendarSettings
            clinicProfile={null}
            availabilityOnly
            targetTherapistId={therapist.id}
            targetTherapistName={therapistName}
            onClose={() => setShowAvailabilityEditor(false)}
          />
        )}

        {/* Appointment Modal */}
        {showAppointmentModal && (
          <AppointmentModal
            appointment={selectedAppointment}
            prefilledDate={prefilledDate}
            patients={patientsData?.patients || []}
            onClose={handleCloseAppointmentModal}
            // Pass target therapist info for admin scheduling
            targetTherapistId={therapist.id}
            targetTherapistName={therapistName}
            scheduledByAdminId={adminTherapistId}
            scheduledByAdminName={adminTherapistName}
            // Callback when appointment is successfully created - refetch appointments and notify parent
            onSuccess={handleAppointmentSuccess}
          />
        )}
      </div>
    </div>
  );
}

export default TherapistCalendarModal;
